import { NgModule, NO_ERRORS_SCHEMA } from '@angular/core';

import { CartSharedModule } from '@spartacus/cart/base/components';
import {
  ConfigModule,
  OccCmsPageNormalizer,
  ProductSearchService,
  provideConfig,
  provideFeatureToggles,
  SearchboxService,
  SiteContextInterceptor,
  SiteContextModule,
  UserAddressAdapter
} from '@spartacus/core';
import {
  BannerModule,
  CarouselModule,
  FormErrorsModule,
  ListNavigationModule,
  NavigationModule,
  NavigationService,
  PageComponentModule,
  PageSlotModule,
  PaginationModule,
  ScrollToTopModule,
  SearchBoxComponentService,
  SearchBoxModule
} from '@spartacus/storefront';
import { UserAccountModule } from '@spartacus/user';

import { DpOccUserAddressAdapter } from '@app/core/adapters/occ-user-address.adapter';
import { layoutConfig } from '@app/core/configs/layout.config';
import { paginationConfig } from '@app/core/configs/pagination.config';
import { routingConfig } from '@app/core/configs/routing.config';
import { DpSiteContextInterceptor } from '@app/core/interceptors/site-context.interceptor';
import { DpOccCmsPageNormalizer } from '@app/core/occ/occ-cms-page-normalizer';
import {
  ANGULAR_GOOGLE_TAG_MANAGER_PROVIDER,
  DP_HTTP_INTERCEPTOR_PROVIDER,
  DP_OVERRIDES_MODULE_INITIALIZER_PROVIDER
} from '@app/core/providers';
import { DpProductSearchService } from '@app/core/services/product-search.service';
import { DpSearchBoxComponentService } from '@app/core/services/search-box-component.service';
import { DpSearchboxService } from '@app/core/services/searchbox.service';
import '@app/core/types/common/module-augmentation';
import { DpAboutUsFeatureModule } from '@app-modules/about-us';
import { DpBannerFeatureModule } from '@app-modules/banner';
import { DpButtonTextMediaFeatureModule } from '@app-modules/button-text-media';
import { DpCartFeatureModule } from '@app-modules/cart';
import { DpCheckoutFeatureModule } from '@app-modules/checkout';
import { DpClickableLinkFeatureModule } from '@app-modules/clickable-link';
import { DpContactUsFeatureModule } from '@app-modules/contact-us';
import { DpDistributorsFeatureModule } from '@app-modules/distributors';
import { DpDownloadsCmsFeatureModule } from '@app-modules/downloads-cms';
import { DpSiteContextEventModule } from '@app-modules/events/site-context-event.module';
import { DpFeedbackBarModule } from '@app-modules/feedback-bar';
import { DpFlexibleContentSectionFeatureModule } from '@app-modules/flexible-content-section';
import { DpImportToolFeatureModule } from '@app-modules/import-tool';
import { DpMediasRowFeatureModule } from '@app-modules/medias-row';
import {
  DpBreadcrumbModule,
  DpFooterNavigationModule,
  DpMyAccountModule,
  DpNavigationModule,
  DpSearchBoxModule
} from '@app-modules/navigation';
import { DpNavigationService } from '@app-modules/navigation/navigation/shared';
import { DpNewsModule } from '@app-modules/news/news.module';
import { DpOrderFeatureModule } from '@app-modules/order';
import { DpPageMetaModule } from '@app-modules/page-meta';
import { DpParagraphFeatureModule } from '@app-modules/paragraph';
import { DpProductFeatureModule } from '@app-modules/product';
import { DpRotatingImagesFeatureModule } from '@app-modules/rotating-images';
import { DpSiteContextModule } from '@app-modules/site-context';
import { DpSocialModule } from '@app-modules/social';
import { DpStoreFinderFeatureModule } from '@app-modules/store-finder';
import { DpTextFormSplitColumnFeatureModule } from '@app-modules/text-form-split-column';
import { DpUserAccountFeatureModule } from '@app-modules/user-account';
import { DpUspFeatureModule } from '@app-modules/usp';
import { DpWishListFeatureModule } from '@app-modules/wish-list';

@NgModule({
  declarations: [],
  providers: [
    { provide: SiteContextInterceptor, useClass: DpSiteContextInterceptor },
    DP_OVERRIDES_MODULE_INITIALIZER_PROVIDER,
    DP_HTTP_INTERCEPTOR_PROVIDER,
    ANGULAR_GOOGLE_TAG_MANAGER_PROVIDER,
    // Custom Services
    { provide: NavigationService, useClass: DpNavigationService },
    { provide: SearchboxService, useClass: DpSearchboxService },
    { provide: ProductSearchService, useClass: DpProductSearchService },
    { provide: UserAddressAdapter, useClass: DpOccUserAddressAdapter },
    { provide: UserAddressAdapter, useClass: DpOccUserAddressAdapter },
    { provide: SearchBoxComponentService, useClass: DpSearchBoxComponentService },
    { provide: OccCmsPageNormalizer, useClass: DpOccCmsPageNormalizer },
    // Custom Outlets
    provideConfig(layoutConfig),
    provideConfig(paginationConfig),
    provideFeatureToggles({
      cmsGuardsServiceUseGuardsComposer: true,
      formErrorsDescriptiveMessages: true,
      propagateErrorsToServer: true,
      ssrStrictErrorHandlingForHttpAndNgrx: true,
      allPageMetaResolversEnabledInCsr: true,
      a11yScrollToTop: true,
      useSiteThemeService: false,
      a11yImproveContrast: false,
      a11yOrganizationsBanner: false,
      a11yFormErrorMuteIcon: false,
      useExtendedMediaComponentConfiguration: true
    })
  ],
  imports: [
    // Standard Modules
    SiteContextModule,
    SearchBoxModule,
    FormErrorsModule,
    CartSharedModule,
    UserAccountModule,
    BannerModule,
    NavigationModule,
    CarouselModule,
    PageSlotModule,
    ListNavigationModule,
    PaginationModule,

    // DP Modules
    DpSiteContextEventModule,
    DpFooterNavigationModule,
    DpSearchBoxModule,
    DpBreadcrumbModule,
    DpNavigationModule,
    DpMyAccountModule,
    DpNewsModule,
    DpParagraphFeatureModule,
    DpDownloadsCmsFeatureModule,
    DpFeedbackBarModule,
    // SAP Storefront Configurations
    DpUserAccountFeatureModule,
    DpUspFeatureModule,
    DpSocialModule,
    DpProductFeatureModule,
    DpSiteContextModule,
    DpAboutUsFeatureModule,
    DpFlexibleContentSectionFeatureModule,
    DpTextFormSplitColumnFeatureModule,
    DpRotatingImagesFeatureModule,
    DpButtonTextMediaFeatureModule,
    DpBannerFeatureModule,
    DpClickableLinkFeatureModule,
    DpCheckoutFeatureModule,
    DpOrderFeatureModule,
    DpDistributorsFeatureModule,
    DpMediasRowFeatureModule,
    DpContactUsFeatureModule,
    DpImportToolFeatureModule,
    ConfigModule.withConfig(routingConfig),
    PageComponentModule,
    DpStoreFinderFeatureModule,
    DpCartFeatureModule,
    DpWishListFeatureModule,
    DpPageMetaModule,
    ScrollToTopModule
  ],
  schemas: [NO_ERRORS_SCHEMA]
})
export class DpCustomStorefrontModule {}
